/**
 * Hubble's law, v = H₀ d, and the quantities that go with it.
 *
 * H₀ is in km/s per megaparsec. Three published values are offered, each with the paper
 * it comes from (all read in October 2026):
 * - Planck 2018, A&A 641, A6 (2020): H₀ = 67.4 ± 0.5, from the cosmic microwave background
 *   assuming base ΛCDM; also Ωₘ = 0.315 ± 0.007;
 * - SH0ES, Riess et al. 2022, ApJ 934, L7: H₀ = 73.04 ± 1.04, from Cepheids and Type Ia
 *   supernovae;
 * - CCHP, Freedman et al. 2025, ApJ 985, 203: H₀ = 70.39 ± 1.22 (stat) ± 1.33 (sys) ±
 *   0.70 (σ_SN), from the tip of the red giant branch with HST and JWST. The three errors
 *   are added in quadrature here, giving ± 1.94.
 * The disagreement between the first two is the "Hubble tension".
 */

import { PARSEC } from "@/lib/tool-kit/astronomy/distance";
import {
  JULIAN_YEAR_SECONDS,
  LIGHT_YEAR,
  SPEED_OF_LIGHT,
} from "@/lib/tool-kit/physics-fields/constants";
import type { UnitDef } from "@/lib/tool-kit/physics-fields/units";

/** The speed of light in km/s (299 792.458, exact). */
export const C_KM_S = SPEED_OF_LIGHT / 1000;

export interface H0Choice {
  id: "planck" | "shoes" | "cchp" | "round";
  /** km/s/Mpc */
  value: number;
  /** One-sigma uncertainty, km/s/Mpc; 0 for the round textbook value. */
  sigma: number;
}

export const H0_CHOICES: readonly H0Choice[] = [
  { id: "planck", value: 67.4, sigma: 0.5 },
  { id: "shoes", value: 73.04, sigma: 1.04 },
  { id: "cchp", value: 70.39, sigma: Math.hypot(1.22, 1.33, 0.7) },
  { id: "round", value: 70, sigma: 0 },
];

/** Planck 2018's matter density parameter, for the comparison with a full ΛCDM distance. */
export const OMEGA_MATTER = 0.315;

/** Distance units as a size in megaparsecs. */
export const HUBBLE_DISTANCE_UNITS: readonly UnitDef[] = [
  { id: "mpc", symbol: "Mpc", factor: 1 },
  { id: "gpc", symbol: "Gpc", factor: 1000 },
  { id: "mly", symbol: "Mly", factor: LIGHT_YEAR / PARSEC },
  { id: "gly", symbol: "Gly", factor: (LIGHT_YEAR * 1e9) / (PARSEC * 1e6) },
];

/** Velocity units as a size in km/s. */
export const HUBBLE_VELOCITY_UNITS: readonly UnitDef[] = [
  { id: "kms", symbol: "km/s", factor: 1 },
  { id: "c", symbol: "c", factor: C_KM_S },
];

/** Recession velocity in km/s for a distance in Mpc. */
export function velocityFromDistance(h0: number, mpc: number): number {
  return h0 * mpc;
}

/** Distance in Mpc for a recession velocity in km/s. */
export function distanceFromVelocity(h0: number, kms: number): number {
  return kms / h0;
}

/** Low-redshift velocity: v = c z. */
export function velocityLowRedshift(z: number): number {
  return C_KM_S * z;
}

/** Relativistic Doppler velocity: v = c ((1+z)² − 1) / ((1+z)² + 1). */
export function velocityRelativistic(z: number): number {
  const s = (1 + z) ** 2;
  return (C_KM_S * (s - 1)) / (s + 1);
}

/** The Hubble time 1/H₀ in billions of years. */
export function hubbleTimeGyr(h0: number): number {
  const seconds = (PARSEC * 1e6) / 1000 / h0;
  return seconds / (JULIAN_YEAR_SECONDS * 1e9);
}

/** The Hubble distance c/H₀ in Mpc. */
export function hubbleDistanceMpc(h0: number): number {
  return C_KM_S / h0;
}

/**
 * The line-of-sight comoving distance to redshift z in a flat ΛCDM universe, in Mpc:
 * (c/H₀) ∫₀ᶻ dz′ / √(Ωₘ(1+z′)³ + 1 − Ωₘ), by Simpson's rule.
 */
export function comovingDistanceMpc(
  z: number,
  h0: number,
  omegaMatter = OMEGA_MATTER
): number {
  const steps = 400;
  const h = z / steps;
  const inverseE = (x: number): number =>
    1 / Math.sqrt(omegaMatter * (1 + x) ** 3 + 1 - omegaMatter);
  let sum = inverseE(0) + inverseE(z);
  for (let i = 1; i < steps; i += 1) {
    sum += (i % 2 === 0 ? 2 : 4) * inverseE(i * h);
  }
  return hubbleDistanceMpc(h0) * ((sum * h) / 3);
}

/** Lookback to the object: the distance in light-years for a distance in Mpc. */
export function mpcToLightYears(mpc: number): number {
  return (mpc * PARSEC * 1e6) / LIGHT_YEAR;
}
